import React, { useState } from 'react';
import classnames from 'classnames';

import styles from './index.module.less';
import type { Tools } from '../../../core/types';
import { Size } from '../../../core/enums';
import withEventTracking from '../../../utilities/withEventTracking';
import { Icon } from '../../atoms/icon';
import Element from '../../../core/Element';

interface FlipperProps {
  mode: string;
  tools?: Tools;
  children: React.ReactElement;
  onClick?: () => void;
  qeId?: string;
  ariaLabelArrowLeft?: string;
  ariaLabelArrowRight?: string;
}

interface AccelerationFilmProps {
  children: React.ReactElement[];
  qeId?: string;
  ariaLabelArrowLeft?: string;
  ariaLabelArrowRight?: string;
  ariaLabelVal?: string;
  tools?: {
    left: Tools;
    right: Tools;
  };
}

const ControlWithTracking = withEventTracking<FlipperProps>(
  ({
    mode,
    children,
    onClick,
    qeId,
    ariaLabelArrowLeft,
    ariaLabelArrowRight,
  }: FlipperProps) => (
    <Element
      type="button"
      Component="button"
      onClick={onClick}
      className={classnames(
        styles.arrow,
        mode === 'left' ? styles.arrowLeft : styles.arrowRight
      )}
      qeId={qeId}
      aria-label={mode === 'left' ? ariaLabelArrowLeft : ariaLabelArrowRight}
    >
      {children}
    </Element>
  )
);
export const AccelerationFilm = ({
  children,
  qeId = 'accelerationFilm',
  ariaLabelArrowLeft = 'Scroll left to view more ways to earn',
  ariaLabelArrowRight = 'Scroll right to view more ways to earn',
  tools,
}: AccelerationFilmProps): React.ReactElement => {
  const [position, setPosition] = useState(0);
  const slideCount = React.Children.count(children);

  const hiddenToScreenReaders = (index: number) => {
    const adjacentSlideCount = 2;
    const isVisible =
      index >= position && index <= position + adjacentSlideCount;
    return !isVisible;
  };

  return (
    <Element qeId={qeId} className={styles.root}>
      <section aria-label={`Benefit list, ${slideCount} items total`}>
        {slideCount > 3 && position !== 0 && (
          <ControlWithTracking
            mode="left"
            tools={
              tools?.left ?? {
                tracking: {
                  methods: ['click'],
                  element: 'accelerator',
                  details: ['carousel', 'left'],
                },
              }
            }
            onClick={() => setPosition(position - 1)}
            qeId={`${qeId}-arrow-left`}
            ariaLabelArrowLeft={ariaLabelArrowLeft}
          >
            <Icon
              imageName="axp-icon-left"
              directory="us-consumer"
              $size={Size.LG}
            />
          </ControlWithTracking>
        )}
        <ul
          className={classnames(styles.list, {
            [styles.listFull]: slideCount > 3,
          })}
          style={{
            margin: 0,
            padding: 0,
            transform: `translateX(-${position * 33.3333}%)`,
            transition: `transform 500ms ease-in-out`,
          }}
          role="none"
        >
          {children.map((item, i) => {
            return (
              <Element
                key={`accelerator-film-${i}`}
                Component="li"
                className={styles.listItem}
                aria-hidden={hiddenToScreenReaders(i)}
                aria-live="assertive"
              >
                <div
                  aria-label={`Benefit ${i + 1} of ${slideCount}`}
                  className={styles.listItemDiv}
                >
                  {item}
                </div>
              </Element>
            );
          })}
        </ul>
        {React.Children.count(children) > 3 &&
          position < React.Children.count(children) - 3 && (
            <ControlWithTracking
              mode="right"
              tools={
                tools?.right ?? {
                  tracking: {
                    methods: ['click'],
                    element: 'accelerator',
                    details: ['carousel', 'right'],
                  },
                }
              }
              onClick={() => setPosition(position + 1)}
              qeId={`${qeId}-arrow-right`}
              ariaLabelArrowRight={ariaLabelArrowRight}
            >
              <Icon
                imageName="axp-icon-right"
                directory="us-consumer"
                $size={Size.LG}
              />
            </ControlWithTracking>
          )}
      </section>
    </Element>
  );
};
